import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { Stack } from 'immutable';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Socket } from 'socket.io-client';

import _fill from 'lodash/fill';
import _intersectionWith from 'lodash/intersectionWith';
import _isEqual from 'lodash/isEqual';
import _uniqBy from 'lodash/uniqBy';
import _cloneDeep from 'lodash/cloneDeep';

import { getAllInvolvedUsersProjects } from '../../api/project/project';
import {
    addMessageToTopic,
    addTopicWithUnreadMessages,
    createTopic,
    getArchivedTopicMessageFeed,
    getArchivedTopicsByParticipant,
    getTopicById,
    getTopicMessageFeed,
    getTopicsByParticipant,
    removeTopicWithUnreadMessages,
} from '../../api/topic';
import { connectToWebSocket, removeListeners } from '../../services/websocket';
import { getCurrentUser, RootState } from '../../store';
import { updateCurrentUserTopicsWithUnreadMessages } from '../../store/actions/ProjectTopicSidebarActions';
import { ITopic, ProjectModel } from '../../store/types';
import { DateFormat, formatDate } from 'susy-common/date-utils';
import { UserRoles } from '../../utils';
import { AppAccordion } from '../shared/appAccordion/AppAccordion';
import SusyButton from '../shared/buttons/SusyButton';
import {
    ProjectTopicEditParticipantsSelectedIcon,
    ProjectTopicEditParticipantsUnselectedIcon,
    ProjectTopicMessageIcon,
    ProjectTopicMessageUnreadIcon,
    RightGrayArrowIcon,
    SlideoutBackButton,
    SlideoutCloseIcon,
} from '../shared/icons/icons';
import IconTextGroup from '../shared/iconTextGroup/IconTextGroup';
import { EButtonStyle } from '../shared/inputWithButton/InputWithButton';
import { AppSelect } from '../shared/Select';
import SusyAvatar, { SuSyAvatarSizes } from '../shared/susyAvatar/SusyAvatar';
import TopicSidebarUtils, {
    mapArchivedTopicToDisplayTopic,
    mapTopicToDisplayTopic,
} from './util';
import SusyFormField from '../shared/forms/SusyFormField/SusyFormField';
import SusyButtonIcon from '../shared/buttons/SusyButonIcon';
import SusyTextArea from '../shared/forms/SusyTextArea';
import { SusyModal } from '../shared/modals/susyModal/SusyModal';
import SusyInput from '../shared/forms/SusyInput';
import { TextAreaWithButton } from '../shared/inputWithButton/TextAreaWithButton';

import { ValidationError } from '../../definitions/interfaces/validationError';
import SearchResults from './SearchResults';
import { SearchBoxMagnifierIcon } from './icons';
import {
    INITIAL_TOPIC_DISPLAY,
    ITopicDisplay,
    ITopicDisplayParticipant,
} from './interfaces';
import { StatusBadge } from '../project/statusBadge/StatusBadge';

import {
    ITopicMessage,
    TopicParticipant,
    ServerToClientEvents,
    ClientToServerEvents,
} from 'susy-common/models';

import './ProjectTopicSidebar.css';
import './SlideoutSidebar.css';

interface Message {
    text: string;
    sentBy: TopicParticipant;
    username: string;
    profilePictureId: string | undefined;
    time: Date;
}

interface ProjectSelection {
    label: string;
    value: string | null;
}

enum TopicIconStatus {
    Open = 'open',
    CloseChat = 'close-chat',
    CloseAllTopics = 'close-all-topics',
}

export enum TopicStatus {
    Active = 'Active',
    Archived = 'Archived',
}

enum ValidationFieldsEnum {
    newTopicName = 'newTopicName',
}

const ProjectTopicSidebar = () => {
    const currentUser = useSelector(
        (state: RootState) => state.auth.currentUser
    );

    const localization = useSelector(
        (state: RootState) =>
            state.localization.localizationStrings.susy.project.projectTopic
                .sidebar
    );

    const dispatch = useDispatch();

    // Topics list state (initial sidebar)
    const [isSidebarVisible, setIsSidebarVisible] = useState<boolean>(false);

    const [availableDisplayTopics, setAvailableDisplayTopics] = useState<
        ITopicDisplay[]
    >([]);
    const [availableProjects, setAvailableProjects] = useState<ProjectModel[]>(
        []
    );
    // New topic form state
    const [isNewTopicFormVisible, setIsNewTopicFormVisible] =
        useState<boolean>(false);
    const [selectedProject, setSelectedProject] =
        useState<ProjectSelection | null>(null);
    const [selectedTopicParticipants, setSelectedTopicParticipants] = useState<
        TopicParticipant[]
    >([]);
    const [topicName, setTopicName] = useState<string>('');

    // Topic chat state
    const [isChatVisible, setIsChatVisible] = useState<boolean>(false);

    const [userTextMessage, setUserTextMessage] = useState<string>('');

    const [openedDisplayedTopic, setOpenedDisplayedTopic] =
        useState<ITopicDisplay>();
    const [openedDisplayedTopicId, setOpenedDisplayedTopicId] =
        useState<string>(''); // Used for signalizing when to fetch messages

    const [messageFeed, setMessageFeed] = useState<Stack<Message>>(
        Stack<Message>()
    );

    const [errors, setErrors] = useState<ValidationError>({});

    const [searchTerm, setSearchTerm] = useState('');

    // Escape closes sidebar
    useEffect(() => {
        const handleEscKeyPress = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setIsSidebarVisible(false);
            }
        };

        window.addEventListener('keydown', handleEscKeyPress);

        // This remove listener on component unmount
        return () => {
            window.removeEventListener('keydown', handleEscKeyPress);
        };
    }, []);

    const fetchUserProjects = async () => {
        const projects = await getAllInvolvedUsersProjects();
        if (projects.length !== 0) {
            setAvailableProjects(projects);
        }
    };

    const fetchAllTopics = async () => {
        const allDisplayTopics: ITopicDisplay[] = [];

        const getTopics = getTopicsByParticipant();
        const getArchivedTopics = getArchivedTopicsByParticipant();

        const allFetchedTopics = await Promise.all([
            getTopics,
            getArchivedTopics,
        ]);

        const allTopics = allFetchedTopics[0];
        const allArchivedTopics = allFetchedTopics[1];

        allDisplayTopics.push(
            ...allTopics.map((topic) => mapTopicToDisplayTopic(topic))
        );

        allDisplayTopics.push(
            ...allArchivedTopics.map((archivedTopic) =>
                mapArchivedTopicToDisplayTopic(archivedTopic)
            )
        );

        setAvailableDisplayTopics(allDisplayTopics);
    };

    useEffect(() => {
        fetchAllTopics();

        fetchUserProjects().catch(toast.error);
    }, []);

    // Set default topic name
    useEffect(() => {
        if (selectedProject) {
            setTopicName(`${selectedProject.label} ${localization.discussion}`);
        }
    }, [selectedProject]);

    // Connect to server socket on component load
    const [socket, setSocket] =
        useState<Socket<ServerToClientEvents, ClientToServerEvents>>();
    useEffect(() => {
        const wsConnect = async () => {
            const socket = await connectToWebSocket();
            setSocket(socket);
        };
        wsConnect().catch(toast.error);

        // Remove listeners on cleanup in order to prevent multiple event registration
        return () => {
            removeListeners(socket);
        };
    }, []);

    // Join notification channel for tracking unread messages
    useEffect(() => {
        if (currentUser?._id !== undefined && socket) {
            socket.emit('joinNotificationChannel', currentUser?._id);
            socket.emit('joinNewTopicChannel', currentUser?._id);
        }
    }, [socket]);

    // Listen for new messages over the sockets
    useEffect(() => {
        socket?.off('message');

        if (socket && openedDisplayedTopic) {
            socket.on('message', (message: ITopicMessage) => {
                const participant = openedDisplayedTopic.participants?.find(
                    (p) => {
                        return p.userId === message.sentBy._id;
                    }
                );
                const fullName = `${participant?.firstName} ${participant?.lastName}`;
                const newMessage = {
                    text: message.text,
                    username: fullName ?? participant?.userId ?? '',
                    sentBy: message.sentBy,
                    profilePictureId: participant?.profilePictureId,
                    time: message.sendTime,
                };

                // https://stackoverflow.com/a/63331455
                setMessageFeed((messageFeed) => {
                    return messageFeed.push(newMessage);
                });
            });
        }
    }, [socket, openedDisplayedTopic]);

    // Listen for new unread messages on all currently logged-in user topics
    useEffect(() => {
        if (socket && currentUser) {
            socket.on(
                'newTopicMessageNotification',
                (topicId: string, message: string) => {
                    const updateUnreadNotifications = () => {
                        const alreadySomeUnreadMessagesInTopic =
                            currentUser?.topicsWithUnreadMessages?.some((t) => {
                                return t === topicId;
                            });
                        if (!alreadySomeUnreadMessagesInTopic) {
                            const tempUser = _cloneDeep(currentUser);
                            tempUser.topicsWithUnreadMessages ??= [];
                            tempUser.topicsWithUnreadMessages.push(topicId);
                            dispatch(
                                updateCurrentUserTopicsWithUnreadMessages(
                                    tempUser
                                )
                            );
                        }
                    };

                    const updateLatestMessage = () => {
                        setAvailableDisplayTopics((topics) => {
                            const topic = topics.find((t) => t._id === topicId);
                            const index = topics.indexOf(
                                topic ?? INITIAL_TOPIC_DISPLAY
                            );
                            if (topic) {
                                topic.latestMessage = {
                                    text: message,
                                };
                                topics[index] = topic;
                            }
                            return topics;
                        });
                    };

                    updateUnreadNotifications();
                    updateLatestMessage();
                }
            );

            socket.on('newTopicNotification', () => {
                fetchAllTopics().catch(toast.error);
            });
        }
    }, [socket, currentUser]);

    // Set currently opened topic and message feed
    const getTopicMessages = async () => {
        if (openedDisplayedTopic?.isArchived) {
            return;
        }

        if (openedDisplayedTopicId) {
            // Get and set topic object
            const topic = await getTopicById(openedDisplayedTopicId);
            setOpenedDisplayedTopic(mapTopicToDisplayTopic(topic));

            // Get and set message feed
            const messageFeedDb = await getTopicMessageFeed(
                openedDisplayedTopicId
            );

            type message = {
                text: string;
                sentBy: TopicParticipant;
                username: string;
                profilePictureId: string;
                time: Date;
            }[];

            const messageFeedMapped = messageFeedDb.messages.map((m) => {
                let username = m.sentBy._id;
                if (
                    m.sentBy.firstName !== undefined &&
                    m.sentBy.lastName !== undefined
                ) {
                    username = `${m.sentBy.firstName} ${m.sentBy.lastName}`;
                }

                return {
                    text: m.text,
                    sentBy: m.sentBy,
                    username: username,
                    profilePictureId: m.sentBy?.profilePictureId ?? '',
                    time: m.sendTime,
                };
            });

            // Set message feed Stack
            const messageFeedReversed = [...messageFeedMapped].reverse();
            setMessageFeed(Stack<Message>(messageFeedReversed));
        }
    };
    useEffect(() => {
        getTopicMessages().catch(toast.error);
    }, [openedDisplayedTopicId]);

    // Set currently opened topic and message feed
    const getArchivedTopicMessages = async () => {
        if (!openedDisplayedTopic?.isArchived) {
            return;
        }

        if (openedDisplayedTopicId) {
            // Get and set message feed
            const messageFeedDb = await getArchivedTopicMessageFeed(
                openedDisplayedTopicId
            );
            const messageFeedMapped = messageFeedDb.messages.map((m) => {
                let username = m.sentBy.userId;
                if (
                    m.sentBy.firstName !== undefined &&
                    m.sentBy.lastName !== undefined
                ) {
                    username = `${m.sentBy.firstName} ${m.sentBy.lastName}`;
                }

                return {
                    text: m.text,
                    sentBy: m.sentBy,
                    username: username,
                    profilePictureId: '',
                    time: m.sendTime,
                };
            });

            // Set message feed Stack
            const messageFeedReversed = [...messageFeedMapped].reverse;
            setMessageFeed(Stack<Message>(messageFeedReversed));
        }
    };
    useEffect(() => {
        // After the user has opened new topic, turn off previous event listeners
        socket?.off('message');
        getArchivedTopicMessages().catch(toast.error);
    }, [openedDisplayedTopicId]);

    useEffect(() => {
        dispatch(getCurrentUser());
    }, [dispatch]);

    const renderTopicChatIcon = () => {
        if (isCurrentUserAdmin() || isSidebarVisible) {
            return <></>;
        }

        const numberOfUnreadMessages = getNumberOfUnreadMessages();
        const getIcon = () => {
            if (numberOfUnreadMessages !== 0) {
                return (
                    <ProjectTopicMessageUnreadIcon
                        numberOfUnreadNotifications={numberOfUnreadMessages}
                    />
                );
            } else {
                return <ProjectTopicMessageIcon />;
            }
        };
        const iconStatus = TopicIconStatus.Open;

        return (
            <SusyButtonIcon
                icon={getIcon()}
                size='lg'
                classes={`susy-chat-button-section susy-topic-chat-icon-${iconStatus}`}
                onClickHandler={() => {
                    setIsSidebarVisible(!isSidebarVisible);
                }}
                dataTestId={`susy-topic-chat-icon-${iconStatus}${
                    numberOfUnreadMessages !== 0 ? '|unread' : ''
                }`}
            />
        );
    };

    const getNumberOfUnreadMessages = (topicId?: string): number => {
        let numberOfMessages;
        if (topicId) {
            numberOfMessages = currentUser?.topicsWithUnreadMessages?.filter(
                (tId) => tId === topicId
            ).length;
        } else {
            numberOfMessages = currentUser?.topicsWithUnreadMessages?.length;
        }

        return numberOfMessages ?? 0;
    };

    const isCurrentUserAdmin = () => {
        return currentUser?.userRole.key === UserRoles.admin;
    };

    const renderTopicChatCloseIcon = () => {
        if (isCurrentUserAdmin() || !isSidebarVisible) {
            return <></>;
        }

        const getIcon = () => {
            return (
                <div>
                    <SlideoutCloseIcon />
                    <div>{localization.close}</div>
                </div>
            );
        };

        const getIconStatus = (): string => {
            let iconStatus = TopicIconStatus.Open;
            if (!isSidebarVisible) {
                return iconStatus;
            } else if (isNewTopicFormVisible || isChatVisible) {
                iconStatus = TopicIconStatus.CloseChat;
            } else if (isSidebarVisible) {
                iconStatus = TopicIconStatus.CloseAllTopics;
            }

            return iconStatus;
        };
        const iconStatus = getIconStatus();

        return (
            <SusyButtonIcon
                icon={getIcon()}
                size='lg'
                classes={`susy-chat-button-section susy-topic-chat-icon-close`}
                onClickHandler={() => {
                    setIsSidebarVisible(!isSidebarVisible);
                }}
                dataTestId={`susy-topic-chat-icon-${iconStatus}`}
            />
        );
    };

    const renderUserAvatar = (userPictureId?: string): ReactElement => {
        return (
            <SusyAvatar
                profilePictureId={userPictureId}
                size={SuSyAvatarSizes.MD}
            />
        );
    };

    const getTopicParticipants = (project?: ProjectModel) => {
        if (!project) {
            return [];
        }
        const participants: Array<TopicParticipant> = [];
        participants.push(
            project.proposedBy ??
                TopicSidebarUtils.getProposedByParticipant(project)
        );

        participants.push(
            project.supervisor ??
                TopicSidebarUtils.getFirstSupervisorParticipant(project)
        );
        participants.push(
            ...TopicSidebarUtils.getSecondSupervisorsParticipants(project)
        );
        participants.push(
            ...TopicSidebarUtils.getTeamMembersParticipants(project)
        );
        // Remove current user since it's the one creating the topic

        return participants.filter((p) => {
            return p._id !== currentUser?._id;
        });
    };

    const newSelectedParticipantFilterNewTopic = (
        participantId: string
    ): TopicParticipant | undefined => {
        const project = availableProjects.find(
            (p) => p._id === selectedProject?.value
        );
        return getTopicParticipants(project).find((p) => {
            return p._id === participantId;
        });
    };

    const onParticipantClick = (
        participantId: string,
        newSelectedParticipantFilter: (
            participantId: string
        ) => TopicParticipant | undefined
    ): void => {
        const selectedParticipant = selectedTopicParticipants.find(
            (p) => p._id === participantId
        );

        // Is already selected?
        if (selectedParticipant) {
            let tmpSelectedParticipants = [...selectedTopicParticipants];
            tmpSelectedParticipants = tmpSelectedParticipants.filter((p) => {
                return p._id !== participantId;
            });
            setSelectedTopicParticipants(tmpSelectedParticipants);
        } else {
            const newSelectedParticipant =
                newSelectedParticipantFilter(participantId);
            if (newSelectedParticipant) {
                setSelectedTopicParticipants([
                    ...selectedTopicParticipants,
                    newSelectedParticipant,
                ]);
            }
        }
    };

    const onParticipantClickNewTopic = (participantId: string): void => {
        onParticipantClick(participantId, newSelectedParticipantFilterNewTopic);
    };

    const renderEditParticipant = (
        participant: TopicParticipant,
        selected: boolean,
        onClick: (
            dataId: string,
            filter?: (data: string) => TopicParticipant
        ) => void,
        projectID: string
    ): JSX.Element => {
        return (
            <IconTextGroup
                key={`${participant._id} - ${projectID}`}
                avatarsOrIcons={[
                    renderUserAvatar(participant.profilePictureId),
                ]}
                text={`${participant.firstName} ${participant.lastName}`}
                flaggedAvatarIndicator={
                    <ProjectTopicEditParticipantsSelectedIcon />
                }
                unflaggedAvatarIndicator={
                    <ProjectTopicEditParticipantsUnselectedIcon />
                }
                classes='susy-project-topic-participant-avatar-edit'
                dataTestId='participant-selection-list'
                dataClick={{
                    onClick: onClick,
                    dataId: participant._id ?? '',
                }}
                selected={selected}
                isClickable={true}
            />
        );
    };

    const renderSidebar = () => {
        return (
            <>
                {renderTopicList()}
                {renderNewTopicForm()}
                {renderChat()}
            </>
        );
    };

    const renderTopicList = () => {
        const renderTitle = () => {
            return (
                <>
                    <div className='susy-topic-sidebar-title'>
                        {localization.allTopics}
                    </div>
                    <hr className='w-100' />
                </>
            );
        };

        const renderSearchBox = () => {
            return (
                <div className='susy-topic-sidebar-search-box d-flex align-items-center'>
                    <SearchBoxMagnifierIcon />
                    <SusyInput
                        value={searchTerm}
                        onChange={(ev) => setSearchTerm(ev.target.value)}
                        dataTestId='topic-search-box'
                    />
                </div>
            );
        };

        const renderNumberOfProjectsLabel = () => {
            return (
                <div className='number-of-projects-container susy-topic-mute-text d-flex justify-content-between align-items-center susy-mb-2'>
                    <div data-testid='number-of-projects'>
                        {
                            _uniqBy(
                                availableDisplayTopics.map((t) => {
                                    const project = t.project as ProjectModel;
                                    if (!project) {
                                        return;
                                    }
                                    return (t.project as ProjectModel)._id;
                                }),
                                (t) => {
                                    return t;
                                }
                            ).length
                        }{' '}
                        {localization.projects}
                    </div>
                </div>
            );
        };

        const renderProjectTopics = () => {
            const renderProjectTopicAccordion = (topics: ITopicDisplay[]) => {
                return (
                    <>
                        {topics.map((renderingTopic: ITopicDisplay) => {
                            const hasUnreadMessages =
                                currentUser?.topicsWithUnreadMessages?.includes(
                                    renderingTopic?._id ?? ''
                                );
                            return (
                                <div
                                    className={`susy-topic-item d-flex ${
                                        openedDisplayedTopicId ===
                                        renderingTopic._id
                                            ? 'opened-topic'
                                            : ''
                                    } ${
                                        hasUnreadMessages
                                            ? 'has-unread-messages'
                                            : ''
                                    }`}
                                    key={renderingTopic._id}
                                    data-testid='topic-list-item'
                                    onClick={() => {
                                        enterTopic(renderingTopic);
                                    }}
                                >
                                    <div className='susy-topic-sidebar-participants-avatar-list'>
                                        {renderParticipantsAvatars(
                                            renderingTopic.participants,
                                            true
                                        )}
                                    </div>

                                    <div className='flex-fill truncating-parent'>
                                        <div className='d-flex justify-content-between'>
                                            <div
                                                className='susy-topic-title'
                                                data-testid='topic-title'
                                            >
                                                {renderingTopic.title}
                                            </div>

                                            <div className='susy-topic-date ml-3 text-right'>
                                                {formatDate(
                                                    renderingTopic.latestMessage
                                                        ?.time ??
                                                        renderingTopic.creationDate,
                                                    DateFormat.MMM_D
                                                )}
                                            </div>
                                        </div>
                                        {renderingTopic.isArchived && (
                                            <StatusBadge
                                                status={TopicStatus.Archived}
                                            />
                                        )}
                                        <div className='d-flex justify-content-between'>
                                            <div className='susy-topic-mute-text truncating-container'>
                                                {renderingTopic.latestMessage
                                                    ?.text ??
                                                    localization.noMessages}
                                            </div>
                                            {hasUnreadMessages && (
                                                <div
                                                    className='susy-topic-unread-notification unread-notification-topic'
                                                    data-testid='unread-notification-topic'
                                                >
                                                    {getNumberOfUnreadMessages(
                                                        renderingTopic._id
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </>
                );
            };

            let topicsByProject: Map<
                string,
                {
                    topics: ITopicDisplay[] | undefined;
                    color: string | undefined;
                }
            > = new Map();

            for (const availableTopic of availableDisplayTopics) {
                const project: ProjectModel =
                    availableTopic.project as ProjectModel;
                if (project._id) {
                    if (topicsByProject.has(project._id)) {
                        topicsByProject = topicsByProject.set(project._id, {
                            color: topicsByProject.get(project._id)?.color,
                            topics: [
                                ...(topicsByProject.get(project._id)?.topics ??
                                    []),
                                availableTopic,
                            ],
                        });
                    } else {
                        const colorOfDisabled = 'var(--susy-neutral-color-80)';
                        topicsByProject = topicsByProject.set(project._id, {
                            color: availableTopic.isArchived
                                ? colorOfDisabled
                                : TopicSidebarUtils.getCalculatedTopicColor(
                                      project.title
                                  ),
                            topics: [availableTopic],
                        });
                    }
                }
            }

            const topicsByProjectsRender: JSX.Element[] = [];
            if (availableDisplayTopics.length === 0) {
                topicsByProjectsRender.push(
                    <div
                        className='no-available-topics-info'
                        key={'no available topic'}
                    >
                        {localization.noAvailableTopics}
                    </div>
                );
                return topicsByProjectsRender;
            }

            const projectHasUnreadMessages = (topics: ITopicDisplay[]) => {
                return (
                    _intersectionWith(
                        currentUser?.topicsWithUnreadMessages,
                        topics?.map((t) => {
                            return t._id;
                        }) ?? [],
                        _isEqual
                    ).length !== 0
                );
            };

            topicsByProject.forEach((topicsWithColour, projectId) => {
                const projectTitle = (
                    availableDisplayTopics.find((t) => {
                        return (t.project as ProjectModel)._id === projectId;
                    })?.project as ProjectModel
                ).title;
                topicsByProjectsRender.push(
                    <div key={projectId}>
                        <AppAccordion
                            headerElement={
                                <div className='susy-topic-project-title'>
                                    <div
                                        className={`susy-topic-sidebar-project-box ${
                                            projectHasUnreadMessages(
                                                topicsWithColour.topics ?? []
                                            )
                                                ? 'project-has-unread-messages'
                                                : ''
                                        }`}
                                        style={{
                                            background: topicsWithColour.color,
                                        }}
                                    ></div>
                                    <div
                                        className='d-flex align-items-center text-left susy-topic-project-subtitle'
                                        data-testid='susy-chat-project-name'
                                    >
                                        {projectTitle}
                                    </div>
                                </div>
                            }
                            bodyElement={renderProjectTopicAccordion(
                                topicsWithColour?.topics ?? []
                            )}
                        />
                    </div>
                );
            });

            return topicsByProjectsRender;
        };

        const enterTopic = async (topic: ITopicDisplay) => {
            const topicId = topic._id;
            if (!topicId) {
                return;
            }

            if (!openedDisplayedTopic?.isArchived) {
                socket?.emit('leaveTopicRoom', openedDisplayedTopicId);
                socket?.emit('joinTopicRoom', topicId);

                if (currentUser?.topicsWithUnreadMessages?.includes(topicId)) {
                    const newUnreadMessagesState =
                        await removeTopicWithUnreadMessages(topicId);
                    const currentUserTmp = currentUser;
                    currentUserTmp.topicsWithUnreadMessages =
                        newUnreadMessagesState;
                    updateCurrentUserTopicsWithUnreadMessages(currentUserTmp);
                }
            }

            setOpenedDisplayedTopicId(() => topicId ?? '');
            setOpenedDisplayedTopic(topic);

            if (!isChatVisible) {
                setIsChatVisible(true);
            }
            if (isNewTopicFormVisible) {
                setIsNewTopicFormVisible(false);
            }
        };

        const renderNewTopicButton = () => {
            return (
                <div
                    className='susy-new-topic-button'
                    data-testid={'susy-new-topic-button'}
                >
                    <SusyButton
                        text={localization.newTopic}
                        size='medium'
                        onClickHandler={() => {
                            setIsNewTopicFormVisible(true);
                            setIsChatVisible(false);
                        }}
                        htmlAttributes={{
                            disabled: availableProjects.length === 0,
                        }}
                    />
                </div>
            );
        };

        return (
            <div className='susy-topic-sidebar-topic-list'>
                {renderTitle()}
                {renderSearchBox()}
                {!searchTerm.trim() && renderNumberOfProjectsLabel()}
                {!searchTerm.trim() && renderProjectTopics()}
                {!!searchTerm.trim() && (
                    <SearchResults
                        searchTerm={searchTerm}
                        enterTopicCallback={enterTopic}
                    />
                )}
                {renderNewTopicButton()}
            </div>
        );
    };

    const renderNewTopicForm = () => {
        const renderParticipantsAndTopicName = () => {
            if (!selectedProject) {
                return;
            }
            const renderTopicNameInput = () => {
                return (
                    <SusyFormField
                        required
                        label={{
                            fieldIdentifier: `topicName`,
                            text: localization.nameTheTopic,
                        }}
                        classes='mt-5'
                    >
                        <SusyTextArea
                            placeholder={localization.chooseTopicName}
                            onChange={(e) => {
                                setTopicName(e.target.value);
                            }}
                            value={topicName}
                            name='topicName'
                            id='topicName'
                            rows={3}
                        />
                        {renderValidationError(
                            ValidationFieldsEnum.newTopicName
                        )}
                    </SusyFormField>
                );
            };
            const renderTopicParticipants = () => {
                const project = availableProjects.find(
                    (p) => p._id === selectedProject?.value
                );
                return (
                    <div className='mt-5'>
                        <SusyFormField
                            required
                            label={{
                                text: localization.selectOneOrMoreParticipants,
                            }}
                        >
                            {getTopicParticipants(project).map(
                                (participant) => {
                                    return renderEditParticipant(
                                        participant,
                                        false,
                                        onParticipantClickNewTopic,
                                        project?._id ?? ''
                                    );
                                }
                            )}
                        </SusyFormField>
                    </div>
                );
            };

            return (
                <div>
                    {renderTopicParticipants()}
                    {renderTopicNameInput()}
                </div>
            );
        };

        const renderNewTopicTitle = () => {
            return (
                <>
                    <div className='susy-topic-sidebar-title'>
                        <div className='susy-slideout-title-container'>
                            <SusyButtonIcon
                                icon={<SlideoutBackButton />}
                                size='lg'
                                onClickHandler={() => {
                                    setIsNewTopicFormVisible(false);
                                }}
                            />
                            {localization.newTopic}
                        </div>
                    </div>
                    <hr className='w-100' />
                </>
            );
        };

        const renderProjectDropdown = () => {
            const projectDropdownOptions: ProjectSelection[] =
                availableProjects?.map((p) => {
                    return {
                        label: p.title,
                        value: p._id,
                    };
                });
            const getProjectValue = (): ProjectSelection[] | null => {
                return selectedProject ? [selectedProject] : null;
            };
            const onSelectProject = (selectedOption: ProjectSelection) => {
                setSelectedTopicParticipants([]);
                setSelectedProject(selectedOption);
            };

            return (
                <SusyFormField
                    required
                    label={{
                        fieldIdentifier: `projectSelect`,
                        text: localization.selectProject,
                    }}
                >
                    <AppSelect
                        value={getProjectValue()}
                        onChange={onSelectProject}
                        options={projectDropdownOptions}
                        placeholder={localization.chooseProject}
                        name='projectSelect'
                        testId='project-select'
                    />
                </SusyFormField>
            );
        };

        const renderStartTopicButton = () => {
            const onStartTopicClick = () => {
                return async () => {
                    if (!validateInputs()) {
                        return;
                    }

                    if (
                        selectedProject?.value &&
                        selectedTopicParticipants.length !== 0
                    ) {
                        const participantsWithCurrentUser =
                            selectedTopicParticipants.map((p) => {
                                return p;
                            });
                        participantsWithCurrentUser.push({
                            _id: currentUser?._id ?? '',
                            firstName: currentUser?.firstName ?? '',
                            lastName: currentUser?.lastName ?? '',
                            profilePictureId:
                                currentUser?.profilePictureId ?? '',
                        });
                        const newTopic: ITopic = {
                            project: selectedProject?.value,
                            title: topicName,
                            participants: participantsWithCurrentUser,
                            creationDate: new Date(),
                        };

                        // Add new topic to db
                        const newTopicDb = await createTopic(newTopic);
                        if (newTopicDb) {
                            setOpenedDisplayedTopicId(
                                newTopicDb.topic._id ?? ''
                            );
                            setOpenedDisplayedTopic(
                                mapTopicToDisplayTopic(newTopicDb.topic)
                            );
                            setIsNewTopicFormVisible(false);
                            setSelectedProject(null);
                        } else {
                            toast.error(localization.failedSavingNewTopic);
                            return;
                        }

                        if (!isChatVisible) {
                            setIsChatVisible(true);
                        }
                        if (isNewTopicFormVisible) {
                            setIsNewTopicFormVisible(false);
                        }

                        // Push new topic to topic list
                        const newAvailableTopics = availableDisplayTopics;
                        newAvailableTopics.push(
                            mapTopicToDisplayTopic(newTopicDb.topic)
                        );
                        setAvailableDisplayTopics(newAvailableTopics);

                        // Join new topic room
                        socket?.emit(
                            'joinTopicRoom',
                            newTopicDb.topic?._id ?? ''
                        );
                        // Notify other participants about new topic
                        socket?.emit(
                            'notifyNewTopic',
                            newTopic.participants.map((p) => p._id)
                        );
                    } else {
                        toast.error(localization.selectedProjectIsNotValid);
                    }
                };
            };

            return (
                <div className='susy-start-topic-button'>
                    <SusyButton
                        text={localization.cancel}
                        size='medium'
                        kind='secondary'
                        onClickHandler={() => {
                            setIsNewTopicFormVisible(false);
                            setIsChatVisible(false);
                        }}
                        classes='mr-3'
                        dataTestId='cancel-new-topic-button'
                    />
                    <SusyButton
                        text={localization.startTopic}
                        size='medium'
                        onClickHandler={onStartTopicClick()}
                        htmlAttributes={{
                            disabled:
                                selectedTopicParticipants.length === 0 ||
                                topicName === '',
                        }}
                        dataTestId='start-topic-button'
                    />
                </div>
            );
        };

        const getClasses = () => {
            const classes = [
                'susy-topic-new-topic-form',
                'susy-modal-dialog-slideout',
            ];
            if (isNewTopicFormVisible) {
                classes.push('show');
            }

            return classes.join(' ');
        };

        return (
            <div
                className={getClasses()}
                data-testid={'susy-topic-new-topic-form'}
            >
                {renderNewTopicTitle()}
                {renderProjectDropdown()}
                {renderParticipantsAndTopicName()}
                {renderStartTopicButton()}
            </div>
        );
    };

    const clearNotificationByTopicId = async () => {
        if (
            currentUser &&
            currentUser.topicsWithUnreadMessages?.includes(
                openedDisplayedTopicId
            )
        ) {
            await removeTopicWithUnreadMessages(openedDisplayedTopicId);
            const updatedUnreadMessages =
                currentUser.topicsWithUnreadMessages?.filter(
                    (twum) => twum !== openedDisplayedTopicId
                ) || [];
            dispatch(
                updateCurrentUserTopicsWithUnreadMessages({
                    ...currentUser,
                    topicsWithUnreadMessages: updatedUnreadMessages,
                })
            );
        }
    };

    const renderChat = () => {
        const renderOtherUserMessage = (
            text: string,
            name: string,
            profilePictureId: string | undefined,
            time: Date
        ) => {
            return (
                <div
                    key={`${time} - ${name}`}
                    className='d-flex align-items-center'
                    data-testid='other-user-message'
                >
                    <div className='susy-mr-1'>
                        {renderUserAvatar(profilePictureId)}
                    </div>
                    <div className='susy-topic-chat-message w-100'>
                        <div className='d-flex justify-content-between'>
                            <div
                                className='susy-bold-text mb-1'
                                data-testid='other-user-message-name'
                            >
                                {name}
                            </div>
                            <div
                                className='user-message-time'
                                data-testid='other-user-message-date'
                            >
                                {formatDate(time, DateFormat.HH_mm)}
                            </div>
                        </div>

                        <div className='d-flex align-items-center justify-content-between'>
                            <div
                                className='susy-topic-message-text'
                                data-testid='other-user-message-text'
                            >
                                {text}
                            </div>
                        </div>
                    </div>
                </div>
            );
        };
        const renderUserMessage = (
            text: string,
            profilePictureId: string | undefined,
            time: Date
        ) => {
            return (
                <div
                    key={`${time} - ${currentUser?.firstName} ${currentUser?.lastName}`}
                    className='d-flex justify-content-end'
                    data-testid='logged-in-user-message'
                >
                    <div className='susy-topic-chat-message-user'>
                        <div
                            className='user-message-time d-flex justify-content-end'
                            data-testid='user-message-date'
                        >
                            {formatDate(time, DateFormat.HH_mm)}
                        </div>
                        <div className='d-flex align-items-center justify-content-between'>
                            <div
                                className='susy-topic-message-text'
                                data-testid='user-message-text'
                            >
                                {text}
                            </div>
                        </div>
                    </div>
                    <div className='susy-ml-1'>
                        {renderUserAvatar(profilePictureId)}
                    </div>
                </div>
            );
        };
        const renderUserChatInput = () => {
            const updateLatestMessageInTopicList = (message: ITopicMessage) => {
                const matchingTopic = availableDisplayTopics.find((t) => {
                    return t._id === openedDisplayedTopic?._id;
                });
                if (matchingTopic) {
                    matchingTopic.latestMessage = {
                        text: message.text,
                        time: message.sendTime,
                    };
                    const matchingTopicIndex =
                        availableDisplayTopics.indexOf(matchingTopic);
                    setAvailableDisplayTopics(
                        _fill(
                            availableDisplayTopics,
                            matchingTopic,
                            matchingTopicIndex,
                            matchingTopicIndex
                        )
                    );
                }
            };
            const sendMessage = async () => {
                if (
                    currentUser?._id &&
                    userTextMessage !== '' &&
                    openedDisplayedTopic?._id
                ) {
                    // Add message to db
                    const message: ITopicMessage = {
                        text: userTextMessage,
                        sentBy: {
                            _id: currentUser._id,
                            firstName: currentUser.firstName,
                            lastName: currentUser.lastName,
                            profilePictureId: currentUser.profilePictureId,
                        },
                        sendTime: new Date(),
                    };
                    const newMessageFeed = await addMessageToTopic(
                        openedDisplayedTopic._id,
                        message
                    );

                    // Notify other participants that they have unread messages
                    const participantIdsWithoutCurrentUser =
                        openedDisplayedTopic.participants
                            .filter((p) => {
                                return p.userId !== currentUser?._id;
                            })
                            .map((p) => {
                                return p.userId;
                            });
                    const unreadTopicMessages =
                        await addTopicWithUnreadMessages(
                            openedDisplayedTopic._id,
                            participantIdsWithoutCurrentUser
                        );
                    if (!unreadTopicMessages) {
                        toast.error(localization.participantsNotNotified);
                    }

                    // Emit message to sockets and update state
                    if (newMessageFeed && socket) {
                        socket.emit('chat', openedDisplayedTopic._id, message);
                        for (
                            let index = 0;
                            index < participantIdsWithoutCurrentUser.length;
                            index++
                        ) {
                            const participantId =
                                participantIdsWithoutCurrentUser[index];
                            socket.emit(
                                'notifyNewTopicMessage',
                                participantId,
                                openedDisplayedTopic._id,
                                message.text
                            );
                        }
                        updateLatestMessageInTopicList(message);
                        setUserTextMessage('');
                    }
                }
            };

            return (
                <div className='d-flex'>
                    <div className='susy-mr-1'>
                        {renderUserAvatar(currentUser?.profilePictureId)}
                    </div>

                    <SusyFormField classes='flex-fill'>
                        <TextAreaWithButton
                            buttonText={<RightGrayArrowIcon />}
                            buttonStyle={EButtonStyle.GHOST}
                            onChange={(e) => {
                                setUserTextMessage(e.target.value);
                            }}
                            onClick={sendMessage}
                            inputValue={userTextMessage}
                            placeholder={localization.enterYourMessage}
                            wrapperClassName='flex-fill'
                        />
                    </SusyFormField>
                </div>
            );
        };
        const renderDateSeparator = (date: Date) => {
            return (
                <div
                    key={`${date}`}
                    className='susy-topic-sidebar-hr date-separator'
                >
                    <hr className='w-100' />
                    <div className='text-nowrap susy-topic-mute-text'>
                        {formatDate(date, DateFormat.MMM_D_COMMA_YYYY)}
                    </div>
                    <hr className='w-100' />
                </div>
            );
        };
        const renderParticipantsList = () => {
            const renderList = (): JSX.Element => {
                return (
                    <div
                        className='susy-topic-chat-participants'
                        data-testid='topic-chat-participants'
                    >
                        <div>
                            {renderParticipantsAvatars(
                                openedDisplayedTopic?.participants
                            )}
                        </div>
                        <div
                            className='d-flex susy-topic-mute-text pl-2'
                            data-testid='number-of-participants'
                        >
                            <div className='susy-bold-text mr-1'>
                                {openedDisplayedTopic?.participants.length ?? 0}
                            </div>
                            <div className='mr-2'>
                                {localization.peopleInThisChat}
                            </div>
                        </div>
                    </div>
                );
            };

            return <>{renderList()}</>;
        };

        const renderTitle = () => {
            return (
                <>
                    <div className='susy-topic-sidebar-title'>
                        <div className='susy-slideout-title-container'>
                            <SusyButtonIcon
                                icon={<SlideoutBackButton />}
                                size='lg'
                                onClickHandler={() => {
                                    setIsChatVisible(false);
                                }}
                            />
                            {openedDisplayedTopic?.title}
                        </div>
                    </div>

                    <hr className='w-100' />
                </>
            );
        };
        const renderChatFeed = () => {
            const wrapper = (children: ReactNode) => {
                return (
                    <>
                        <div
                            className='susy-topic-chat-feed'
                            data-testid='topic-chat-feed'
                        >
                            {children}
                        </div>
                        <hr className='susy-topic-sidebar-hr' />
                    </>
                );
            };

            if (messageFeed.size === 0) {
                return wrapper(
                    <div className='d-flex justify-content-center'>
                        {localization.noMessages}
                    </div>
                );
            }

            let messageFeedByDate: Map<string, { messages: Message[] }> =
                new Map();
            messageFeed.map((message) => {
                if (message) {
                    const date = formatDate(
                        message.time,
                        DateFormat.MMM_D_COMMA_YYYY
                    );
                    if (messageFeedByDate.has(date)) {
                        messageFeedByDate = messageFeedByDate.set(date, {
                            messages: [
                                ...(messageFeedByDate.get(date)?.messages ??
                                    []),
                                message,
                            ],
                        });
                    } else {
                        messageFeedByDate = messageFeedByDate.set(date, {
                            messages: [message],
                        });
                    }
                }
            });

            const messageFeedRender: JSX.Element[] = [];
            messageFeedByDate.forEach((value, date) => {
                messageFeedRender.push(renderDateSeparator(new Date(date)));
                messageFeedRender.push(
                    ...value.messages.map((message) => {
                        if (message.sentBy._id === currentUser?._id) {
                            return renderUserMessage(
                                message?.text,
                                message?.profilePictureId,
                                message?.time
                            );
                        } else {
                            return renderOtherUserMessage(
                                message?.text,
                                message?.username,
                                message?.profilePictureId,
                                message?.time
                            );
                        }
                    })
                );
            });

            return wrapper([...messageFeedRender].reverse());
        };

        const getClasses = () => {
            const classes = ['susy-topic-chat', 'susy-modal-dialog-slideout'];
            if (isChatVisible) {
                classes.push('show');
            }

            return classes.join(' ');
        };

        return (
            <div
                onMouseEnter={clearNotificationByTopicId}
                className={getClasses()}
                data-testid='topic-chat'
            >
                {renderTitle()}
                {renderParticipantsList()}
                {renderChatFeed()}
                {!openedDisplayedTopic?.isArchived && renderUserChatInput()}
            </div>
        );
    };

    const renderParticipantsAvatars = (
        participants: ITopicDisplayParticipant[] | undefined,
        shouldExcludeCurrentUser = false
    ) => {
        if (!participants) {
            return null;
        }

        const participantsForDisplaying = shouldExcludeCurrentUser
            ? participants.filter((p) => p.userId !== currentUser?._id)
            : participants;

        const participantPictures = participantsForDisplaying.map((p) => (
            <SusyAvatar
                key={p.profilePictureId}
                profilePictureId={p.profilePictureId}
                size={SuSyAvatarSizes.SM}
            />
        ));

        const iconTextGroup = (
            <IconTextGroup
                avatarsOrIcons={participantPictures}
                text={''}
                classes='susy-project-topic-participant-avatar'
                isClickable={false}
            />
        );

        return iconTextGroup;
    };

    const validateInputs = () => {
        const foundErrors: ValidationError = {};

        const sanitizedTopicName = topicName.trim();

        if (!sanitizedTopicName) {
            foundErrors[ValidationFieldsEnum.newTopicName] = {
                message: localization.errorMessages.topicNameCantBeEmpty,
            };
        }

        const allExistingTopicNames = availableDisplayTopics.map(
            (topic) => topic.title
        );
        if (allExistingTopicNames.includes(sanitizedTopicName)) {
            foundErrors[ValidationFieldsEnum.newTopicName] = {
                message: localization.errorMessages.topicNameIsNotUnique,
            };
        }

        setErrors(foundErrors);

        const noErrorsFound = Object.keys(foundErrors).length === 0;
        return noErrorsFound;
    };

    const renderValidationError = (key: string) => {
        return (
            errors[key] && (
                <div>
                    <span
                        data-testid='error-msg'
                        className='asterisk mt-2'
                    >{`* ${errors[key].message}`}</span>
                </div>
            )
        );
    };

    return (
        <>
            {renderTopicChatCloseIcon()}
            <SusyModal
                showModal={isSidebarVisible}
                onHide={() => {
                    setIsNewTopicFormVisible(false);
                    setIsSidebarVisible(false);
                }}
                classes='susy-topic-sidebar'
                dialogClasses='modal-dialog-slideout'
                modalBody={renderSidebar()}
                dataTestId='susy-topic-sidebar'
            />
            {renderTopicChatIcon()}
        </>
    );
};

export default ProjectTopicSidebar;
